import React from "react";

import { RichTextBlock as BaseRichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./rich-text.module.scss";

type TerminalMargin = "both" | "none" | "firstChild" | "lastChild";

interface Props extends React.ComponentProps<typeof BaseRichTextBlock> {
    /**
     * Usually we want the margin-top of the rich text's first child and the
     * margin-bottom of the rich text's last child to be trimmed so they don't
     * affect the layout. But there are some cases where it's useful, so here
     * are a few ways out if you need them.
     */
    trimTerminalMargins?: TerminalMargin;
    applyLengthBasedStyles?: boolean;
    /**
     * There are multiple spots on the site where we want headings to take up
     * the full width alloted to the rich text but we want the paragraph width
     * to be capped at something less.
     */
    constrainParagraphWidth?: "half" | "two-thirds" | "full" | null;
}

export function RichText({
    trimTerminalMargins = "both",
    applyLengthBasedStyles = false,
    constrainParagraphWidth = null,
    ...props
}: Props) {
    const characterCountThreshold = 500;
    const lengthBasedStyle =
        applyLengthBasedStyles && props.value.length < characterCountThreshold
            ? styles.conciseContent
            : styles.lengthyContent;

    return (
        <BaseRichTextBlock
            {...props}
            className={concatClassNames([
                styles.richText,
                styles[trimTerminalMargins],
                lengthBasedStyle,
                constrainParagraphWidth === "half" ? styles.constrainHalf : "",
                constrainParagraphWidth === "two-thirds"
                    ? styles.constrainTwoThirds
                    : "",
                props.className,
            ])}
        />
    );
}
